import React from 'react';
import { IField, IGroundTruth, IDocument, IToken } from '../../../types/my-types';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { AppContext } from '../../../App';
import MarkField from '../../tool/form/MarkField';
import { DataTypeOption } from '../../fields/FieldsSettings/FieldsSettingsTypes';
import { useGroundTruthCreate } from '../../hooks/useGroundTruthCreate';

import { useGroundTruthUpdate } from '../../hooks/useGroundTruthUpdate';
import { useGroundTruthDelete } from '../../hooks/useGroundTruthDelete';
import errorResponses from '../../../errorResponses';
import {
  markValidationByDocumentAtom,
  markShowValidationsAtom,
} from '../../../atoms/mark-validation-atoms';
import { IValidationErrorByExtractionDetails } from '../../../types/validation-types';
export interface GroundTruthFieldProps {
  doc: IDocument;
  field: IField;
  fieldsCount: number;
  fieldId: string;
  index: number;
  groundTruth?: IGroundTruth;
  fieldDataType?: string;
  fieldDataTypeConfig: DataTypeOption | null | undefined;
  trainingSetId: string;
  fields: IField[];
}

const GroundTruthField = (props: GroundTruthFieldProps) => {
  const {
    doc,
    field,
    fields,
    fieldId,
    fieldsCount,
    index,
    groundTruth,
    fieldDataType,
    fieldDataTypeConfig,
    trainingSetId,
  } = props;
  const { setToast } = React.useContext(AppContext);
  const validationByDocument = useAtomValue(markValidationByDocumentAtom);
  const showValidations = useAtomValue(markShowValidationsAtom);

  const cache = useQueryClient();

  // Get validation errors for this field from the validation atom
  const validationErrorsForThisField: IValidationErrorByExtractionDetails[] = React.useMemo(() => {
    if (!showValidations) return [];
    const docValidation = validationByDocument.find((v) => v.documentId === doc.id);
    if (!docValidation) return [];
    return docValidation.validationErrorDetails
      .filter((e) => e.field === field.label)
      .map((e) => ({
        id: e.id,
        field: e.field,
        error: e.error,
        errorDetail: e.errorDetail,
        type: e.type,
      }));
  }, [validationByDocument, doc.id, field.label, showValidations]);

  const mutationGroundTruthCreate = useGroundTruthCreate(true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ground Truth Saved',
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths'],
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['groundTruths'],
      });
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToSaveGroundTruth,
      });
    },
  });

  const mutationGroundTruthUpdate = useGroundTruthUpdate(true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ground Truth Saved',
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths'],
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['groundTruths'],
      });
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToSaveGroundTruth,
      });
    },
  });

  const mutationGroundTruthDelete = useGroundTruthDelete(true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ground Truth Deleted.',
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths'],
      });
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToDeleteGroundTruth,
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths'],
      });
    },
  });

  const initValue = groundTruth ? groundTruth.content : '';
  const pageIndex = groundTruth?.token_data.tokens[0] && groundTruth.token_data.tokens[0].page - 1;

  const saveGroundTruth = async (value: string, tokens: IToken[]) => {
    try {
      if (groundTruth) {
        if (value.length === 0) {
          await mutationGroundTruthDelete.mutate({
            trainingSetId,
            document: doc.id,
            mutationData: { id: groundTruth.id },
          });
        } else if (value !== groundTruth.content || tokens.length > 0) {
          const token_data = tokens.length === 0 ? groundTruth.token_data : { tokens };

          await mutationGroundTruthUpdate.mutate({
            trainingSetId,
            id: groundTruth.id,
            mutationData: {
              document: doc.id,
              content: value,
              token_data,
            },
          });
        }
      } else if (value.length !== 0) {
        await mutationGroundTruthCreate.mutate({
          trainingSetId,
          mutationData: {
            field: field.id,
            document: doc.id,
            content: value,
            token_data: { tokens },
          },
        });
      }
    } catch (error) {
      setToast({
        variant: 'error',
        open: true,
        message: 'Sorry, failed to save ground truth',
      });
    }
  };

  const latestSaveGroundTruth = React.useRef(saveGroundTruth);
  latestSaveGroundTruth.current = saveGroundTruth;
  const save = React.useCallback(
    (value: string, tokens: IToken[]) => latestSaveGroundTruth.current(value, tokens),
    []
  );

  const pageIdx = pageIndex !== undefined ? pageIndex : -1;

  return (
    <MarkField
      tsId={trainingSetId}
      field={field.label}
      fields={fields}
      fieldsCount={fieldsCount}
      fieldDataType={fieldDataType}
      fieldDataTypeConfig={fieldDataTypeConfig}
      fieldId={fieldId}
      glyntTags={field.glynt_tags}
      validationConfig={field.validation_config}
      validationErrorsForThisField={validationErrorsForThisField}
      index={index}
      pageIndex={pageIdx}
      initValue={initValue || ''}
      save={save}
      isMutationLoading={
        mutationGroundTruthCreate.isPending ||
        mutationGroundTruthUpdate.isPending ||
        mutationGroundTruthDelete.isPending
      }
      isMutationError={
        mutationGroundTruthCreate.isError ||
        mutationGroundTruthUpdate.isError ||
        mutationGroundTruthDelete.isError
      }
      isReview={false}
    />
  );
};

export default React.memo(GroundTruthField);
